import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu, X, Settings as SettingsIcon } from 'lucide-react';
import SettingsSidebar from '../../components/settings/SettingsSidebar';
import { SETTINGS_NAV } from '../../components/settings/settingsNav';
import { useSettingsStore } from '../../store/settingsStore';
import { useTranslation } from '../../hooks/useTranslation';
import { cn } from '../../utils/helpers';

/**
 * Settings shell. Renders a persistent category rail on desktop and a slide-in
 * drawer on mobile, with the active category/sub-page in the <Outlet />.
 *
 * This route lives under `/app`, which is already wrapped by <ProtectedRoute />,
 * so unauthenticated users can never reach it.
 */
export default function SettingsLayout() {
  const location = useLocation();
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hydrate = useSettingsStore((s) => s.hydrate);

  // Load settings once when entering the section.
  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const current = [...SETTINGS_NAV]
    .sort((a, b) => b.to.length - a.to.length)
    .find((i) => location.pathname === i.to || location.pathname.startsWith(i.to + '/'));

  return (
    <div className="mx-auto max-w-6xl">
      {/* Mobile top bar */}
      <div className="lg:hidden flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <SettingsIcon className="w-5 h-5 text-neutral-700" />
          <span className="font-bold text-lg">{current ? t(current.labelKey) : t('settings.title')}</span>
        </div>
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label={t('settings.openMenu')}
          className="p-2.5 rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-950"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      <div className="flex gap-8">
        {/* Desktop rail */}
        <aside className="hidden lg:block w-72 shrink-0">
          <div className="sticky top-8 space-y-4">
            <div className="px-3">
              <h1 className="text-2xl font-bold tracking-tight">{t('settings.title')}</h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">{t('settings.subtitle')}</p>
            </div>
            <div className="bg-white dark:bg-neutral-900 rounded-3xl border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none p-3">
              <SettingsSidebar />
            </div>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 pb-16">
          <Outlet />
        </main>
      </div>

      {/* Mobile drawer */}
      <div className={cn('lg:hidden fixed inset-0 z-50', drawerOpen ? '' : 'pointer-events-none')}>
        <div
          className={cn('absolute inset-0 bg-neutral-900/40 backdrop-blur-sm transition-opacity', drawerOpen ? 'opacity-100' : 'opacity-0')}
          onClick={() => setDrawerOpen(false)}
        />
        <div
          className={cn(
            'absolute inset-y-0 left-0 w-80 max-w-[85%] bg-neutral-50 dark:bg-neutral-950 shadow-2xl p-4 overflow-y-auto transition-transform duration-300',
            drawerOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex items-center justify-between mb-4 px-1">
            <h2 className="text-lg font-bold">{t('settings.title')}</h2>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label={t('settings.closeMenu')}
              className="p-2 rounded-xl hover:bg-neutral-200 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <SettingsSidebar onNavigate={() => setDrawerOpen(false)} />
        </div>
      </div>
    </div>
  );
}
